import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FolderTree, ArrowRight, ClipboardCheck, Lightbulb, Trash2 } from "lucide-react";

/**
 * EmployeeAssignedProjects — every research project this employee/intern is
 * involved in, plus the lightweight task workspaces they've spun up from the
 * Task Board.
 *
 * Sources merged into the "Research projects" tab:
 *   • `/guidance/mine`  — guidance workspaces where the user owns, mentors,
 *     or is a participant (filters out task-derived ones).
 *   • `/ideas`          — ideas the user owns, is an active collaborator on,
 *     or is the assigned mentor for (backend now includes mentor
 *     assignments via `linked_mentor_id`).
 */
export default function EmployeeAssignedProjects() {
  const { user } = useAuth();
  const [guidance, setGuidance] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("projects"); // "projects" | "tasks"
  useEffect(() => {
    Promise.all([
      api.get("/guidance/mine").catch(() => ({ data: [] })),
      api.get("/ideas").catch(() => ({ data: [] })),
      api.get("/employee/tasks").catch(() => ({ data: [] })),
    ]).then(([g, i, t]) => {
      setGuidance(g.data || []);
      setIdeas(i.data || []);
      setTasks(t.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/employee/tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
      toast.success("Task deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openTaskWorkspace = async (t) => {
    if (t.guidance_id) { window.location.href = `/dashboard/tasks/${t.guidance_id}`; return; }
    try {
      const r = await api.post(`/employee/tasks/${t.id}/workspace`);
      if (r.data?.guidance_id) window.location.href = `/dashboard/tasks/${r.data.guidance_id}`;
    } catch { toast.error("Couldn't open workspace"); }
  };

  // Research projects = (real guidance projects, i.e. not task-derived) + (ideas I'm involved with).
  const guidanceProjects = guidance.filter((g) =>
    g.type !== "employee_task"
    && (g.mentor_user_id === user?.user_id
        || (g.participants || []).includes(user?.user_id)
        || g.user_id === user?.user_id)
  ).map((g) => ({
    id: g.id,
    href: `/dashboard/guidance/${g.id}`,
    kind: "guidance",
    label: "GUIDANCE PROJECT",
    title: g.problem_description || "Untitled guidance",
    status: g.status,
    owner: g.user_name,
    deadline: g.deadline,
    myRole: g.mentor_user_id === user?.user_id ? "MENTOR" : (g.user_id === user?.user_id ? "OWNER" : "COLLABORATOR"),
  }));
  const ideaProjects = ideas.map((i) => ({
    id: i.id,
    href: `/dashboard/ideas/${i.id}`,
    kind: "idea",
    label: "RESEARCH IDEA",
    title: i.title || i.problem_statement || "Untitled idea",
    status: i.status || i.stage || "in_progress",
    owner: i.user_name,
    deadline: null,
    myRole: i.is_assigned_mentor ? "MENTOR" : (i.is_collaborator ? "COLLABORATOR" : "OWNER"),
  }));
  const projects = [...guidanceProjects, ...ideaProjects];

  const list = tab === "projects" ? projects : tasks;

  return (
    <div data-testid="employee-assigned-projects-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Assigned <em className="italic text-kb-primary">work</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Research ideas &amp; guidance workspaces where you own, mentor, or collaborate — plus the lightweight workspaces spun up from your Task Board.</p>

      <div className="mt-6 inline-flex border border-slate-200 rounded-sm bg-white">
        {[
          { key: "projects", label: `Research projects (${projects.length})` },
          { key: "tasks", label: `Task workspaces (${tasks.length})` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs kb-mono uppercase tracking-wider ${tab === t.key ? "bg-kb-ink text-white" : "text-kb-ink-muted hover:text-kb-ink"}`}
            data-testid={`assigned-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        list.length === 0 ? (
          <div className="mt-8 text-center py-12 border border-dashed border-slate-300 rounded-sm bg-white">
            <FolderTree className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-kb-ink-muted">
              {tab === "projects" ? "No assigned research projects yet." : "No task workspaces yet — open a task from your board to spin one up."}
            </p>
          </div>
        ) : tab === "projects" ? (
          <div className="mt-6 space-y-3">
            {list.map((p) => (
              <Link
                key={`${p.kind}-${p.id}`}
                to={p.href}
                className="kb-card p-5 flex flex-wrap items-center justify-between gap-3 hover:border-kb-primary transition-colors"
                data-testid={`assigned-project-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-2">
                    {p.kind === "idea" ? <Lightbulb className="w-3 h-3 text-amber-500" /> : <ClipboardCheck className="w-3 h-3 text-kb-primary" />}
                    {p.label} · {p.status}{p.owner ? ` · ${p.owner}` : ""}
                    <span className={`ml-1 px-1.5 py-0.5 rounded-sm border text-[9px] ${p.myRole === "MENTOR" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : p.myRole === "OWNER" ? "border-slate-200 bg-slate-50 text-slate-700" : "border-sky-200 bg-sky-50 text-sky-700"}`}>
                      {p.myRole}
                    </span>
                  </div>
                  <div className="text-base font-medium text-kb-ink mt-1 line-clamp-2">{p.title.slice(0, 160)}</div>
                  {p.deadline && <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">Due {p.deadline}</div>}
                </div>
                <div className="text-kb-primary inline-flex items-center gap-2 text-sm shrink-0">
                  Open <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {tasks.map((t) => (
              <div
                key={t.id}
                className="kb-card p-5 flex flex-wrap items-center justify-between gap-3 hover:border-kb-primary transition-colors"
                data-testid={`assigned-project-${t.id}`}
              >
                <button
                  type="button"
                  onClick={() => openTaskWorkspace(t)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                    TASK · {t.status || "todo"}{t.assigner_name ? ` · by ${t.assigner_name}` : ""}{t.priority ? ` · ${t.priority}` : ""}
                  </div>
                  <div className="text-base font-medium text-kb-ink mt-1 truncate">{t.title || "Untitled task"}</div>
                  {t.due_date && <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">Due {t.due_date}</div>}
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openTaskWorkspace(t)}
                    className="text-kb-primary inline-flex items-center gap-2 text-sm"
                    data-testid={`assigned-project-${t.id}-open`}
                  >
                    <ClipboardCheck className="w-4 h-4" /> Open <ArrowRight className="w-4 h-4" />
                  </button>
                  {(user?.user_id === t.assigner_user_id || ["admin", "super_admin"].includes(user?.role)) && (
                    <button
                      type="button"
                      onClick={() => deleteTask(t)}
                      className="p-1.5 text-rose-500 hover:text-rose-700"
                      title="Delete task"
                      data-testid={`assigned-project-${t.id}-delete`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
